/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useMemo } from "react";
import "./Sidenav.scss";
import { NavLink, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import { Grid2 } from "@mui/material";
import { DndProvider } from "react-dnd";
import FolderTree from "../../features/folder/FolderTree";
import { HTML5Backend } from "react-dnd-html5-backend";
import CloseIcon from "@mui/icons-material/Close";
import { useMenu } from "../../context/MenuContext";
import {
  ModuleNames,
  POSTIF_STATIC_UI_BYPASS,
  POSTIF_STATIC_UI_NAV_LINKS,
  WORKFLOW_STATIC_UI_BYPASS,
  WORKFLOW_STATIC_UI_NAV_LINKS,
} from "../../data/constants";
import { getFilteredMenu } from "../../utils/commonHelper";
import { MenuSection } from "../../types/Menu";
import useWorkflowInProgressCount from "../../hooks/useWorkflowInProgressCount";

interface SidenavProps {
  isOpen: boolean;
  onClose: () => void;
  isMobile: boolean;
}

const POSTIF_NAV_MODULES = new Set<string>([ModuleNames.PostIFMyForms, ModuleNames.PostIFList]);
const POSTIF_NAV_PATH = "/hiring/postif";
const WORKFLOW_WORK_NAV_MODULES = new Set<string>([
  ModuleNames.WorkflowInstances,
  ModuleNames.WorkflowMyWork,
]);
// Epic 80431: /workflow/my-work renders the same screen instead of redirecting, so the one collapsed
// row (linked to /workflow/instances) has to light up on both paths.
const WORKFLOW_WORK_NAV_PATHS = new Set<string>(["/workflow/instances", "/workflow/my-work"]);

/**
 * Every in-portal PostIF screen (list, new, edit, approval) lives under this href.
 * NavLink's default `end` only marks the exact list URL as active, so the Hiring
 * item went grey on /new and /:id. Prefix-match those routes; leave `end` on
 * every other item so a short path cannot light up an unrelated sibling.
 */
const isPostIfNavItem = (url: string): boolean =>
  url === POSTIF_NAV_PATH || url.startsWith(`${POSTIF_NAV_PATH}/`);

const collapsePostIfNav = (sections: MenuSection[]): MenuSection[] =>
  sections
    .map((section) => {
      const postifItems = section.listItems.filter((item) => POSTIF_NAV_MODULES.has(item.moduleName));
      // Epic 82838: one "Post Interview List" row whenever ANY PostIF module is granted - not only
      // when both are. With a single grant the seeded display name ("My Post Interview Forms" /
      // "Post Interview Forms") used to show, while the page band and breadcrumbs say "Post
      // Interview List" (PR #32635 review).
      if (postifItems.length === 0) {
        return section;
      }

      return {
        ...section,
        listItems: [
          ...section.listItems.filter((item) => !POSTIF_NAV_MODULES.has(item.moduleName)),
          {
            ...postifItems[0],
            title: "Post Interview List",
            url: "/hiring/postif",
          },
        ],
      };
    })
    .filter((section) => section.listItems.length > 0);

// One menu row for both panels. The database may still return two module rows.
const collapseWorkflowWorkNav = (sections: MenuSection[]): MenuSection[] =>
  sections.map((section) => {
    const workspaceItems = section.listItems.filter((item) =>
      WORKFLOW_WORK_NAV_MODULES.has(item.moduleName),
    );
    if (workspaceItems.length === 0) {
      return section;
    }

    return {
      ...section,
      listItems: [
        ...section.listItems.filter(
          (item) => !WORKFLOW_WORK_NAV_MODULES.has(item.moduleName),
        ),
        {
          ...workspaceItems[0],
          title: "Instances & My Work",
          url: "/workflow/instances",
          icon: "/workflow-instances.svg",
          moduleName: ModuleNames.WorkflowInstances,
        },
      ],
    };
  });

const NAV_SECTIONS_BEFORE_ADMIN = ["Hiring", "Workflow Management"] as const;

// Prototype order: Library, Builder, Test / Run, then Instances and My Work.
// A database menu can return Instances first; missing static links are appended after it.
const orderWorkflowNav = (sections: MenuSection[]): MenuSection[] => {
  const order: readonly string[] = WORKFLOW_STATIC_UI_NAV_LINKS.map((link) => link.url);
  return sections.map((section) => {
    if (section.title !== "Workflow Management") {
      return section;
    }

    const listItems = [...section.listItems].sort((left, right) => {
      const leftIndex = order.indexOf(left.url);
      const rightIndex = order.indexOf(right.url);
      const leftRank = leftIndex === -1 ? order.length : leftIndex;
      const rightRank = rightIndex === -1 ? order.length : rightIndex;
      return leftRank - rightRank;
    });

    return { ...section, listItems };
  });
};

const reorderSectionsBeforeAdmin = (sections: MenuSection[]): MenuSection[] => {
  const pinnedTitles = new Set<string>(NAV_SECTIONS_BEFORE_ADMIN);
  const pinned = NAV_SECTIONS_BEFORE_ADMIN
    .map((title) => sections.find((section) => section.title === title))
    .filter((section): section is MenuSection => Boolean(section));

  if (pinned.length === 0) {
    return sections;
  }

  const remainder = sections.filter((section) => !pinnedTitles.has(section.title));
  const adminIndex = remainder.findIndex((section) => section.title === "Admin");

  if (adminIndex < 0) {
    return [...remainder, ...pinned];
  }

  return [...remainder.slice(0, adminIndex), ...pinned, ...remainder.slice(adminIndex)];
};

interface StaticNavLink {
  title: string;
  url: string;
  moduleName: string;
  icon?: string;
}

const appendStaticSection = (
  sections: MenuSection[],
  links: readonly StaticNavLink[],
  sectionId: number,
  sectionTitle: string,
  icon: string,
): MenuSection[] => {
  const existingUrls = new Set(
    sections.flatMap((section) => section.listItems.map((item) => item.url)),
  );
  // Dedupe by URL. Test/Run is not its own module (Epic 80427, RouteKeywords.WorkflowBuilder);
  // it is a second link on WorkflowBuilder, and a module-name check would drop it.
  const missingLinks = links.filter((link) => !existingUrls.has(link.url));

  if (missingLinks.length === 0) {
    return sections;
  }

  const existingSectionIndex = sections.findIndex((section) => section.title === sectionTitle);
  if (existingSectionIndex >= 0) {
    return sections.map((section, index) =>
      index === existingSectionIndex
        ? {
            ...section,
            listItems: [
              ...section.listItems,
              ...missingLinks.map((link, linkIndex) => ({
                id: sectionId + linkIndex + 1,
                title: link.title,
                url: link.url,
                icon: link.icon ?? icon,
                moduleName: link.moduleName,
              })),
            ],
          }
        : section,
    );
  }

  return [
    ...sections,
    {
      id: sectionId,
      title: sectionTitle,
      icon,
      listItems: missingLinks.map((link, index) => ({
        id: sectionId + index + 1,
        title: link.title,
        url: link.url,
        icon: link.icon ?? icon,
        moduleName: link.moduleName,
      })),
    },
  ];
};

const Sidenav: React.FC<SidenavProps> = ({ isOpen, onClose, isMobile }) => {
  const { menu, loading, permissions } = useMenu();
  const location = useLocation();
  const filteredMenu = getFilteredMenu(menu);
  // Epic 80431: in-progress cases in the user's scope, refreshed on navigation and every ~60s.
  // Only asked for by someone who can view Instances - anyone else would just get a 403.
  const canViewInstances =
    WORKFLOW_STATIC_UI_BYPASS ||
    Boolean(permissions?.find((permission) => permission.moduleName === ModuleNames.WorkflowInstances)?.canView);
  const workflowInProgressCount = useWorkflowInProgressCount(canViewInstances, location.pathname);

  const displayMenu = useMemo((): MenuSection[] => {
    let sections = filteredMenu;

    if (POSTIF_STATIC_UI_BYPASS) {
      sections = appendStaticSection(
        sections,
        POSTIF_STATIC_UI_NAV_LINKS,
        9990,
        "Hiring",
        "/activities.svg",
      );
    }

    if (WORKFLOW_STATIC_UI_BYPASS) {
      sections = appendStaticSection(
        sections,
        WORKFLOW_STATIC_UI_NAV_LINKS,
        10000,
        "Workflow Management",
        "/workflow-library.svg",
      );
    }

    return reorderSectionsBeforeAdmin(
      orderWorkflowNav(collapseWorkflowWorkNav(collapsePostIfNav(sections))),
    );
  }, [filteredMenu]);

  return (
    // Bug 82554 - DndProvider must mount exactly once for the lifetime of the app. It used to sit
    // inside `!loading && displayMenu.map(...)`, so every MenuContext permission refetch (which
    // flips `loading` true then false) unmounted and remounted it. HTML5Backend.setup() guards on
    // the global `window.__isReactDndBackendSetUp` flag, and the three pieces that clear it run in
    // three different React phases - the singleton manager is read during render, the backend
    // teardown in a layout-effect cleanup, and the `window[INSTANCE_SYM] = null` reset in a passive
    // effect cleanup that React 18 defers. When a remount out-raced that deferred reset a second
    // backend was constructed and setup() threw "Cannot have two HTML5 backends at the same time",
    // which the ErrorBoundary turned into a full-page error. Sidenav itself is rendered
    // unconditionally by App.tsx, so mounting the provider here keeps it stable across refetches.
    // Do not move this back inside the menu render.
    <DndProvider backend={HTML5Backend}>
      {/* Backdrop for mobile */}
      {isMobile && isOpen && (
        <div className="sidenav-backdrop" onClick={onClose}>
          <CloseIcon className="mobile-close-icon" />
        </div>
      )}

      <div
        className={`sidenav ${isMobile ? "mobile-sidenav" : "desktop-sidenav"} ${isOpen ? "open" : ""}`}
      >
        <aside className="aside">
          <div className="navbar-brand-box">
            <img src="/logo-transparent.svg" alt="logo" className="logo" />
            <span className="application-name">Employee Portal</span>
          </div>

          <Box component="nav" className="sidenav-navigation" aria-label="Main navigation">
            <Grid2 container spacing={0}>
              <Grid2 size={12}>
                <div className="menu">
                  {!loading && displayMenu.map((item) => (
                    <div className="item" key={item.id}>
                      <span className="title">{item.title}</span>
                      {item.listItems.map((listItem) => {
                        if (listItem.moduleName === ModuleNames.FileExplorer) {
                          // Render File Explorer section. The DndProvider that FolderTree needs is
                          // mounted once at the root of this component - see the Bug 82554 note there.
                          return (
                            <React.Fragment key={listItem.id}>
                              <FolderTree />
                            </React.Fragment>
                          );
                        }

                        // Render standard menu items
                        return (
                          <NavLink
                            to={listItem.url}
                            end={!isPostIfNavItem(listItem.url)}
                            className={({ isActive }) =>
                              `listItem ${
                                isActive ||
                                (WORKFLOW_WORK_NAV_MODULES.has(listItem.moduleName) &&
                                  WORKFLOW_WORK_NAV_PATHS.has(location.pathname))
                                  ? "active"
                                  : ""
                              }`
                            }
                            key={listItem.id}
                            onClick={() => {
                              if (isMobile) onClose();
                            }}
                          >
                            <img src={listItem.icon} alt="" />
                            <span className="listItemTitle">{listItem.title}</span>
                            {listItem.moduleName === ModuleNames.WorkflowInstances &&
                              workflowInProgressCount > 0 && (
                                <span
                                  className="listItemBadge"
                                  aria-label={`${workflowInProgressCount} in-progress workflow instances`}
                                >
                                  {workflowInProgressCount}
                                </span>
                              )}
                          </NavLink>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </Grid2>
            </Grid2>
          </Box>
        </aside>
      </div>
    </DndProvider>
  );
};

export default Sidenav;
